import { useCallback, useState, type ReactNode } from "react";
import { Button, ConfirmDialog, Field, TextArea } from "@odingard/design-system";
import { ApiError } from "@odingard/sdk-client";
import { usePermissions } from "./usePermissions";
import { describeDenial, type GovernancePermission } from "./permissions";

export interface GovernedMutation {
  run: (fn: () => Promise<unknown>) => Promise<boolean>;
  busy: boolean;
  error: string | null;
  reset: () => void;
}

/**
 * Runs one governed backend call, keeping the backend's own denial text.
 *
 * A 403 is recorded against the permission so the action stops being offered,
 * and its message — which names the missing permission — is shown as-is rather
 * than being rewritten into "something went wrong".
 */
export function useGovernedMutation(permission: GovernancePermission): GovernedMutation {
  const { observeDenial } = usePermissions();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true);
      setError(null);
      try {
        await fn();
        return true;
      } catch (err: unknown) {
        if (err instanceof ApiError) {
          observeDenial(permission, err);
          setError(describeDenial(err));
        } else {
          setError("Unexpected error.");
        }
        return false;
      } finally {
        setBusy(false);
      }
    },
    [observeDenial, permission],
  );

  return { run, busy, error, reset: () => setError(null) };
}

export interface GovernedActionProps {
  permission: GovernancePermission;
  /** Trigger label. */
  label: string;
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  busyLabel?: string;
  destructive?: boolean;
  /** What this decision does that cannot be undone. Rendered above the form. */
  consequence?: ReactNode;
  requireRationale?: boolean;
  rationaleLabel?: string;
  rationaleHelp?: string;
  children?: ReactNode;
  onConfirm: (rationale: string) => Promise<unknown>;
  onDone?: () => void;
  triggerVariant?: "primary" | "secondary" | "ghost" | "danger";
  triggerSize?: "sm" | "md" | "lg";
}

/**
 * The shared confirmation pattern for every governed decision (verify, reject,
 * approve, revoke, freeze…): permission-gated trigger, modal confirmation that
 * states the consequence, an optional required rationale, and inline surfacing
 * of whatever the backend answers.
 */
export function GovernedAction({
  permission,
  label,
  title,
  description,
  confirmLabel,
  busyLabel,
  destructive = false,
  consequence,
  requireRationale = false,
  rationaleLabel = "Rationale",
  rationaleHelp,
  children,
  onConfirm,
  onDone,
  triggerVariant,
  triggerSize = "sm",
}: GovernedActionProps) {
  const { can } = usePermissions();
  const verdict = can(permission);
  const [open, setOpen] = useState(false);
  const [rationale, setRationale] = useState("");
  const mutation = useGovernedMutation(permission);

  const close = () => {
    setOpen(false);
    setRationale("");
    mutation.reset();
  };

  const confirm = async () => {
    const ok = await mutation.run(() => onConfirm(rationale.trim()));
    if (!ok) return;
    close();
    onDone?.();
  };

  return (
    <>
      <Button
        variant={triggerVariant ?? (destructive ? "danger" : "secondary")}
        size={triggerSize}
        onClick={() => setOpen(true)}
        disabled={!verdict.allowed}
        aria-describedby={verdict.allowed ? undefined : `${permission}-reason`}
      >
        {label}
      </Button>
      {!verdict.allowed && (
        <span className="ods-gate__reason" id={`${permission}-reason`}>
          {verdict.reason}
        </span>
      )}

      <ConfirmDialog
        open={open}
        title={title}
        description={description}
        confirmLabel={confirmLabel}
        {...(busyLabel ? { busyLabel } : {})}
        destructive={destructive}
        busy={mutation.busy}
        disabled={requireRationale && rationale.trim().length === 0}
        error={mutation.error}
        onConfirm={() => void confirm()}
        onCancel={close}
      >
        {consequence ? (
          <p className="ods-denial" style={{ marginTop: 0 }}>
            {consequence}
          </p>
        ) : null}
        {children}
        {requireRationale ? (
          <Field label={rationaleLabel} {...(rationaleHelp ? { help: rationaleHelp } : {})}>
            {(id) => (
              <TextArea
                id={id}
                rows={3}
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
              />
            )}
          </Field>
        ) : null}
      </ConfirmDialog>
    </>
  );
}
